import React, { useState } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Paper, Button, Chip, Tooltip, IconButton, Dialog, 
  DialogTitle, DialogContent, DialogActions, LinearProgress
} from "@mui/material";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import Swal from "sweetalert2";

const brand = "#800b33";
const brandHover = "#6c0a2b";

const stages = ["Chairperson", "Katibu", "Muhasibu", "Disbursement"];

const ChairpersonLoanApprovals = () => {
  const [loans, setLoans] = useState([
    { id: 1, name: "Asha Juma", amount: 100000, method: "Daily", status: "Pending", currentStage: "Chairperson", date: "2025-10-28" },
    { id: 2, name: "John Paulo", amount: 80000, method: "Weekly", status: "Pending", currentStage: "Chairperson", date: "2025-10-26" },
    { id: 3, name: "Neema Peter", amount: 120000, method: "Daily", status: "Approved", currentStage: "Katibu", date: "2025-10-20" },
  ]);

  const [selectedLoan, setSelectedLoan] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const handleView = (loan) => {
    setSelectedLoan(loan);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedLoan(null);
  };

  const handleApprove = (loan) => {
    Swal.fire({
      title: "Approve Loan?",
      text: `Approve loan for ${loan.name} — ${loan.amount.toLocaleString()} TZS?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: brand,
      confirmButtonText: "Yes, Approve",
    }).then((res) => {
      if (res.isConfirmed) {
        const nextStage = getNextStage(loan.currentStage);
        setLoans((prev) =>
          prev.map((l) =>
            l.id === loan.id
              ? {
                  ...l,
                  status: nextStage ? "In Progress" : "Approved",
                  currentStage: nextStage || "Disbursement",
                }
              : l
          )
        );
        Swal.fire({
          icon: "success",
          title: `Loan forwarded to ${nextStage || "Disbursement"}`,
          text: `${loan.name}'s loan now moves to the next stage.`,
          confirmButtonColor: brand,
        });
      }
    });
  };

  const getNextStage = (current) => {
    const i = stages.indexOf(current);
    return i < stages.length - 1 ? stages[i + 1] : null;
  };

  const handleReject = (loan) => {
    Swal.fire({
      title: "Reject Loan?",
      input: "text",
      inputPlaceholder: "Enter reason for rejection",
      inputValidator: (value) => (!value ? "Rejection reason required!" : undefined),
      showCancelButton: true,
      confirmButtonColor: "#d32f2f",
      confirmButtonText: "Reject",
    }).then((res) => {
      if (res.isConfirmed && res.value) {
        setLoans((prev) =>
          prev.map((l) =>
            l.id === loan.id ? { ...l, status: "Rejected", reason: res.value } : l
          )
        );
        Swal.fire({
          icon: "info",
          title: "Loan Rejected",
          text: `Reason: ${res.value}`,
          confirmButtonColor: brand,
        });
      }
    });
  };

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.15, duration: 0.4 },
    }),
  };

  const progressValue = (stage) => ((stages.indexOf(stage) + 1) / stages.length) * 100;

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Typography variant="h5" sx={{ fontWeight: 900, color: brand, mb: 1 }}>
        Loan Approvals — Chairperson
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
        Review, approve, or reject member loan applications.
      </Typography>

      <motion.div variants={fadeIn} initial="hidden" animate="visible">
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Member</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Amount (TZS)</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Repayment</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stage</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loans.map((loan, i) => (
                    <TableRow key={loan.id} hover>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{loan.name}</TableCell>
                      <TableCell>{loan.amount.toLocaleString()}</TableCell>
                      <TableCell>{loan.method}</TableCell>
                      <TableCell>{loan.currentStage}</TableCell>
                      <TableCell>
                        <Chip
                          label={loan.status}
                          color={
                            loan.status === "Approved"
                              ? "success"
                              : loan.status === "Rejected"
                              ? "error"
                              : "warning"
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton onClick={() => handleView(loan)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        {loan.status !== "Rejected" &&
                          loan.currentStage === "Chairperson" && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton color="success" onClick={() => handleApprove(loan)}>
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton color="error" onClick={() => handleReject(loan)}>
                                  <CancelIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </motion.div>

      {/* Loan Details Dialog */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Loan Details
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedLoan && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography><strong>Member:</strong> {selectedLoan.name}</Typography>
              <Typography><strong>Amount:</strong> {selectedLoan.amount.toLocaleString()} TZS</Typography>
              <Typography><strong>Method:</strong> {selectedLoan.method}</Typography>
              <Typography><strong>Date:</strong> {selectedLoan.date}</Typography>
              <Typography><strong>Status:</strong> {selectedLoan.status}</Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: brand, fontWeight: 700 }}>
                  Approval Chain Progress
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={progressValue(selectedLoan.currentStage)}
                  sx={{
                    height: 10,
                    borderRadius: 5,
                    bgcolor: "#f2f2f2",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        selectedLoan.status === "Rejected"
                          ? "#e53935"
                          : selectedLoan.status === "Approved"
                          ? "#2e7d32"
                          : brand,
                    },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  {stages.map((s) => (
                    <Typography
                      key={s}
                      variant="caption"
                      color={
                        stages.indexOf(s) <= stages.indexOf(selectedLoan.currentStage)
                          ? "text.primary"
                          : "text.disabled"
                      }
                    >
                      {s}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChairpersonLoanApprovals;
